import { IconButton, InputAdornment } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import React, { useState } from "react";
import {
  FieldTitle,
  InputHelperText,
  ResettableTextField,
  useTranslate,
  ValidationErrorMessage,
} from "react-admin";
import { Controller, FieldValues } from "react-hook-form";
import { HookFormInputProps } from "./HookFormInputProps";

interface Props<TRecord> extends HookFormInputProps<TRecord> {
  initiallyVisible?: boolean;
  options?: any;
  margin?: "none" | "dense" | "normal";
  variant?: "standard" | "outlined" | "filled";
}

declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

const HookFormPasswordInputComponent = <TRecord extends FieldValues>(
  {
    control,
    name,
    defaultValue,
    label,
    rules,
    initiallyVisible = false,
    margin,
    variant,
    options,
  }: Props<TRecord>,
  ref: React.Ref<unknown>
) => {
  const [visible, setVisible] = useState(initiallyVisible);
  const translate = useTranslate();

  const handleClick = () => {
    setVisible(!visible);
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue ?? ""}
      rules={rules}
      render={(renderProps) => {
        return (
          <ResettableTextField
            {...options}
            source={name}
            type={visible ? "text" : "password"}
            label={
              label !== "" &&
              label !== false && (
                <FieldTitle
                  label={label}
                  source={name}
                  isRequired={!!rules?.required}
                />
              )
            }
            error={
              !!(
                renderProps.fieldState.isTouched && renderProps.fieldState.error
              )
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={translate(
                      visible
                        ? "ra.input.password.toggle_visible"
                        : "ra.input.password.toggle_hidden"
                    )}
                    onClick={handleClick}>
                    {visible ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              <InputHelperText
                touched={renderProps.fieldState.isTouched}
                error={
                  (renderProps.fieldState.error as any)
                    ?.message as ValidationErrorMessage
                }
                helperText={
                  (renderProps.fieldState.error as any)?.message as string
                }
              />
            }
            variant={options?.variant ?? variant}
            margin={options?.margin ?? margin}
            {...renderProps.field}
            ref={ref}
            inputRef={renderProps.field.ref}
          />
        );
      }}
    />
  );
};

export const HookFormPasswordInput = React.forwardRef(
  HookFormPasswordInputComponent
);
